import type { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  createContext,
  createRef,
  type PropsWithChildren,
  type RefObject,
  use,
  useState,
} from 'react';
import type { TextInput } from 'react-native';
import type { EnrichedTextInputInstance } from 'react-native-enriched-html';
import type { RequireAtLeastOne } from 'type-fest';
import {
  createWithEqualityFn,
  useStoreWithEqualityFn,
} from 'zustand/traditional';
import type { ChatMessageActionOption } from './Chat.utils';

export type ChatMessageActionSheetData = ChatMessageActionOption[] & {
  content?: string | null;
  isImage?: boolean;
};

export type ChatMessageActionSheetModal =
  BottomSheetModal<ChatMessageActionSheetData>;

export type ChatMessageDetailSheetModal = BottomSheetModal<undefined>;

type ChatDetailProps = {
  selectedMessageId: string | null;
  pressedId: string | null;
  hasNewerMessages: boolean;
  isMediaSelectOpen: boolean;
  isAtNewest: boolean;
  canLoadMore: boolean;
  bottomSheetRef: RefObject<ChatMessageActionSheetModal | null>;
  detailSheetRef: RefObject<ChatMessageDetailSheetModal | null>;
  inputRef: RefObject<EnrichedTextInputInstance | null>;
  fakeInputRef: RefObject<TextInput | null>;
};

type ChatDetailState = ChatDetailProps & {
  setContext: (value: RequireAtLeastOne<ChatDetailProps>) => void;
};

type ChatDetailStore = ReturnType<typeof createChatDetailStore>;
type ChatDetailProviderProps = PropsWithChildren<ChatDetailProps>;

const ChatDetailContext = createContext<ChatDetailStore | null>(null);

const bottomSheetRef = createRef<ChatMessageActionSheetModal>();
const detailSheetRef = createRef<ChatMessageDetailSheetModal>();
const inputRef = createRef<EnrichedTextInputInstance>();
const fakeInputRef = createRef<TextInput>();

const initialChatDetailState: ChatDetailProps = {
  selectedMessageId: null,
  pressedId: null,
  hasNewerMessages: false,
  isMediaSelectOpen: false,
  isAtNewest: true,
  canLoadMore: false,
  bottomSheetRef,
  detailSheetRef,
  inputRef,
  fakeInputRef,
};

const createChatDetailStore = (initProps?: Partial<ChatDetailProps>) => {
  const DEFAULT_PROPS: ChatDetailProps = initialChatDetailState;

  return createWithEqualityFn<ChatDetailState>()((set) => ({
    ...DEFAULT_PROPS,
    ...initProps,
    setContext: (value) => set(() => ({ ...value })),
  }));
};

export function ChatDetailProvider({
  children,
  ...props
}: ChatDetailProviderProps) {
  const [store] = useState(() => createChatDetailStore(props));

  return (
    <ChatDetailContext.Provider value={store}>
      {children}
    </ChatDetailContext.Provider>
  );
}

export { initialChatDetailState };

export function useChatDetailContext<T>(
  selector: (state: ChatDetailState) => T,
  equalityFn?: (left: T, right: T) => boolean,
): T {
  const store = use(ChatDetailContext);
  if (!store) throw new Error('Missing ChatDetailContext.Provider in the tree');
  return useStoreWithEqualityFn(store, selector, equalityFn);
}
